<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <span class="flex-grow-1" v-if="name"><b>{{ $t('occurenceIntro') }}</b> {{name}}</span>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <div class="flex-column-responsive gap-1">
                    <!-- Photo and map-->
                    <div id="photo-map-container" class="d-flex flex-column px-3">
                        <div role="group" class="form-group p-3">
                            <div class="d-flex justify-content-between mb-1">
                                <label class=""> {{ $t('photoMandatory') }}</label>
                                <button id="show-default-img-popup" @click="showDefaultImagePopUp = true">{{ $t('pickDefault') }}</button>
                            </div>
                            <GalleryPicker class="gallery-picker" v-model="occurence.photos" :ratioWidth="16" :ratioHeight="9" v-bind:class="{'is-invalid': genericValidator(occurence.photo) === false || errorValidator('Photo') === false }"></GalleryPicker>
                        </div>
                        <GmapMap :center="center" :zoom="zoom" style="width: 100%; height: 100%;">
                            <GmapMarker v-if="occurencePosition" :position="occurencePosition" :draggable="true" @dragend="getMarkerPosition($event.latLng)"/>
                        </GmapMap>
                    </div>
                    <div class="d-flex flex-wrap flex-grow-1">
                        <!-- Names -->
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('nameMandatory') }}</label>
                            <CInput type="text" v-model="occurence.names[currentLanguage.key]" :invalid-feedback="errorOutput['Names']" :is-valid="genericValidator(occurence.names) && errorValidator('Names')"></CInput>
                        </div>
                        <!-- Description -->
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('descriptionMandatory') }}</label>
                            <VueEditor v-model="occurence.descriptions[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(occurence.descriptions) == false || errorValidator('Descriptions') }"></VueEditor>
                            <div v-if="errorOutput['Descriptions']" class="invalid-feedback">{{errorOutput['Descriptions']}}</div>
                        </div>
                        <div class="col-12 d-flex flex-wrap">
                            <!-- Status -->
                            <div role="group" class="form-group flex-grow-1 mr-1">
                                <label class=""> {{ $t('typeMandatory') }}</label>
                                <SingleOptionDropdown v-model="occurence.type" :options="types" :title="$t('type')" :index="1" :is-valid="errorValidator('Type')" :invalid-feedback="errorOutput['Type']"></SingleOptionDropdown>
                            </div>
                            <!-- User -->
                            <div role="group" class="form-group flex-grow-1 ml-1">
                                <label class=""> {{ $t('usersMandatory') }}</label>
                                <SingleOptionDropdown v-model="occurence.mainUserId" :options="users" :title="$t('users')" :index="2" :is-valid="errorValidator('Users')" :invalid-feedback="errorOutput['Users']"></SingleOptionDropdown>
                            </div>
                        </div>
                        <!-- Tags -->
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('tagsMandatory') }}</label>
                            <vue-tags-input v-model="tag" :placeholder="$t('addTag')" :tags="occurence.tags[currentLanguage.key]" @tags-changed="newTags => occurence.tags[currentLanguage.key] = newTags.map(x => x.text)"  v-bind:class="{'is-invalid': genericValidator(occurence.tags[currentLanguage.key], triedSubmit) == false || errorValidator('Tags') }" class="flex-grow-1"/>
                            <div v-if="genericValidator(occurence.tags[currentLanguage.key], triedSubmit) === false" class="invalid-feedback d-block">{{errorOutput['Tags']}}</div>
                        </div>
                        <div class="col-12 d-flex flex-wrap">
                            <!-- Start time -->
                            <div role="group" class="form-group flex-grow-1 mr-1">
                                <label class=""> {{ $t('startTimeMandatory') }}</label>
                                <CInput type="datetime-local" v-model="occurence.startTime" :invalid-feedback="errorOutput['StartTime']" :is-valid="genericValidator(occurence.startTime) && errorValidator('StartTime')"></CInput>
                            </div>
                            <!-- End time -->
                            <div role="group" class="form-group flex-grow-1 ml-1">
                                <label class=""> {{ $t('endTimeMandatory') }}</label>
                                <div class="d-flex">
                                    <CInputCheckbox class="mr-3" :checked='hasEndTime' @update:checked="hasEndTime = !hasEndTime"></CInputCheckbox>
                                    <CInput type="datetime-local" class="flex-grow-1" :readonly="!hasEndTime" v-model="occurence.endTime" :invalid-feedback="errorOutput['EndTime']" :is-valid="errorValidator('EndTime')"></CInput>
                                </div>
                            </div>
                        </div>
                        <div class="col-12 d-flex flex-wrap">
                            <CButton type="button" class="btn btn-secondary btn-coordinates mr-1" @click="resetCoordinates">{{$t("resetCoordinates")}}</CButton>
                            <!-- Latitude -->
                            <div role="group" class="form-group flex-grow-1 mr-1">
                                <label class=""> {{ $t('latitudeMandatory') }}</label>
                                <CInput type="number" v-model="occurence.latitude" :invalid-feedback="errorOutput['Latitude']" :is-valid="genericValidator(occurence.latitude) && errorValidator('Latitude')" @change="coordinateChanged"></CInput>
                            </div>
                            <!-- Longitude -->
                            <div role="group" class="form-group flex-grow-1 ml-1">
                                <label class=""> {{ $t('longitudeMandatory') }}</label>
                                <CInput type="number" v-model="occurence.longitude" :invalid-feedback="errorOutput['Longitude']" :is-valid="genericValidator(occurence.longitude) && errorValidator('Longitude')" @change="coordinateChanged"></CInput>
                            </div>
                        </div>
                        <!-- Address -->
                        <div role="group" class="form-group col-12">
                            <label class=""> {{ $t('location') }}</label>
                            <!-- <CInput type="text" v-model="occurence.address" :invalid-feedback="errorOutput['Address']" :is-valid="errorValidator('Address')"></CInput> -->
                            <CInput type="text" v-model="occurence.address" @input="delayedGeocodeAddress" :invalid-feedback="errorOutput['Address']" :is-valid="errorValidator('Address')"></CInput>
                            <!-- <p>Latitude: {{ occurence.latitude }}</p>
                            <p>Longitude: {{ occurence.longitude }}</p> -->
                        </div>
                    </div>
                </div>
                <!-- Action button + submitting -->  
                <div role="group" class="form-group col-12 d-flex justify-content-end">
                    <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                    <CButton v-if="!submitting" type="button" class="btn btn-secondary mr-1" @click="submitClicked(false)">{{ $t('acceptChanges') }}</CButton>
                    <CButton v-if="!submitting && occurence.status != 'Pending'" type="button" class="btn btn-primary ml-1" @click="submitClicked(true)">{{ $t('acceptChangesAndLeave') }}</CButton>
                    <CButton v-if="!submitting && occurence.status == 'Pending'" type="button" class="btn btn-primary ml-1" @click="submitClicked(true)">{{ $t('changeAndPublish') }}</CButton>
                </div>
                <!-- submitting -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
    <DefaultImagePopUp @imagePicked="onImagePicked" @closeConfirmRequested="showDefaultImagePopUp = false" v-model="showDefaultImagePopUp"></DefaultImagePopUp>
</div>
</template>
 
<script>
import Occurence from "@/classes/Occurence";
import { mapState, mapActions } from 'vuex'
import LanguageDropdown from "@/controls/LanguageDropdown.vue";
import { CONSTANTS } from '@/helpers/consts';
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import DefaultImagePopUp from "@/controls/popups/DefaultImagePopUp.vue";
import GalleryPicker from '@/controls/GalleryPicker.vue'
import { VueEditor } from "vue2-editor";
import FormLoading from '@/controls/FormLoading.vue'
import InputGetList from "@/classes/InputGetList";
import { genericValidator, errorValidator, deepClone } from "@/helpers/utils.js";
import { API_HELPER } from "@/helpers/api.js";
import SingleOptionDropdown from '@/controls/SingleOptionDropdown.vue'
import VueTagsInput from '@johmun/vue-tags-input';
// import func from 'vue-editor-bridge';
// import * as VueGoogleMaps from 'vue2-google-maps';

const DEFAULT_LATITUDE = 41.30530368251521;
const DEFAULT_LONGITUDE = -7.739819379133223;
const DEFAULT_ADDRESS = "A Voz de Trás-os-Montes, Avenida Aureliano Barrigas, Boxes, Vila Real, 5000-413, Portugal";
const PENDING_STATUS = "Pending";

  export default {
  components: { LanguageDropdown, ConfirmationPopUp, DefaultImagePopUp, GalleryPicker, VueEditor, FormLoading, SingleOptionDropdown, VueTagsInput },
    props:{    
        baseOccurence:
        {
            type: Object,
            default: null,
        },
        baseOccurenceName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
        requestOccurence: {
            type: Boolean,
            default: false
        },
    },
    data()
    {
        return {
            triedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            occurence: new Occurence({}, {}, {}, [], DEFAULT_LATITUDE, DEFAULT_LONGITUDE, DEFAULT_ADDRESS),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
            showDefaultImagePopUp: false,
            tag:"",
            leaveRequested: false,
            delayTimer: null,
            hasEndTime: false,
            setup: false
        }
    },
    mounted: function()
    {
        this.resetOccurence();
        //Make request
        const pageSize = 999;
        const pageNum = 1;
        var input = new InputGetList(pageSize, pageNum, null, 1, "firstName");
        this.getUsers(input); 
    },
    computed:
    {
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        ...mapState('occurences', ['postOccurenceOutput']),
        ...mapState('occurences', ['putOccurenceOutput']),
        ...mapState('nomatim', ['getAddressOutput']),
        ...mapState('users', ['getUsersOutput']),
        errorOutput: function()
        {
            var result = [];

            if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
            {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate multilanguage fields
            for (const [key, value] of Object.entries(this.occurence.names)) { if(!value) { result['Names'] = this.$i18n.t("validateAllTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.occurence.descriptions)) { if(!value) { result['Descriptions'] = this.$i18n.t("validateAllTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.occurence.tags)) { if(!value || !value.length) { result['Tags'] = this.$i18n.t("validateAllTranslations"); break; }; }

            return result;
        },
        name: function()
        {
            return this.baseOccurence || this.baseOccurenceName ? (this.baseOccurence ? this.baseOccurence.name : this.baseOccurenceName) : null;
        },
        center: function(){
            return this.occurencePosition ? this.occurencePosition : {lat: 39.9838329, lng: -6.09731 };
        },
        zoom: function() {
            return this.occurencePosition ? 10 : 5;
        },
        occurencePosition: function(){
            return this.occurence && this.occurence.latitude && this.occurence.longitude ? { lat: parseFloat(this.occurence.latitude), lng: parseFloat(this.occurence.longitude) } : null;
        },
        types: function()
        {
            return [{key: "Event", label: this.$i18n.t("event") }, {key: "Accident", label: this.$i18n.t("accident") }, {key: "Stop", label: this.$i18n.t("stop") }, {key: "Radar", label: this.$i18n.t("radar") }, {key: "Roadblocks", label: this.$i18n.t("roadblocks") }, {key: "Fires", label: this.$i18n.t("fires") }, {key: "Other", label: this.$i18n.t("other") } ];
        },
        usersInfo: function() {
            return this.getUsersOutput ? this.getUsersOutput : null;
        },
        users: function()
        {
            var result = [];

            if(this.usersInfo && this.usersInfo.data)
            {
                this.usersInfo.data.forEach(user =>
                {
                    result.push({key: user.id, label: `${user.firstName} ${user.lastName} (${user.email})`});
                });
            }
            
            return result;
        },
    },
    methods:{
        ...mapActions('users', ['getUsers']),
        ...mapActions('nomatim', ['getAddress']),
        getMarkerPosition(val){
            this.occurence.latitude = val.lat();
            this.occurence.longitude = val.lng();

            this.getAddress({latitude: this.occurence.latitude, longitude: this.occurence.longitude});
        },
        resetOccurence(){
            if(!this.baseOccurence)
            {
                var names = {}; var descriptions = {}; var tags = {};
                CONSTANTS.supportedLanguages.forEach(language => { names[language.key] = ""; descriptions[language.key] = ""; tags[language.key] = []; });

                this.occurence = new Occurence(names, descriptions, tags, [], DEFAULT_LATITUDE, DEFAULT_LONGITUDE, DEFAULT_ADDRESS);
            }
            else
            {
                this.occurence = deepClone(new Occurence, this.baseOccurence);    

                this.occurence.photos = [];
                this.occurence.photoPaths.forEach(photoPath =>
                {
                    this.occurence.photos.push({url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, photoPath])});    
                })

                this.hasEndTime = this.occurence.endTime != null;
            }

            this.setup = true;
        },
        submitClicked(leave){
            this.leaveRequested = leave ?? false;
            this.triedSubmit = true;

            // Emit event
            if(this.occurence.isValid())
            {
                this.submitting = true;
                var input = deepClone(new Object, this.occurence);
                if(!this.hasEndTime)
                {
                    input.endTime = null;
                }

                this.$emit('submit-requested', input);
            }
            else
            {
                alert(this.$i18n.t("invalidInfoError"));
            }
        },
        resetCoordinates(){
            this.occurence.longitude = DEFAULT_LONGITUDE;
            this.occurence.latitude = DEFAULT_LATITUDE;
            this.occurence.address = DEFAULT_ADDRESS;
        },
        delayedGeocodeAddress() {
            clearTimeout(this.delayTimer);
            this.delayTimer = setTimeout(() => {
                this.geocodeAddress();
            }, 2000); 
        },
        geocodeAddress() {
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ address: this.occurence.address }, (results, status) => {
                if (status === 'OK') {
                    this.occurence.latitude = results[0].geometry.location.lat();
                    this.occurence.longitude = results[0].geometry.location.lng();
                } else {
                    console.log('Geocode was not successful for the following reason: ' + status);
                    this.occurence.latitude = '';
                    this.occurence.longitude = '';
                }
            });
        },
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;    
            if(this.leaveRequested)
            {
                if(this.occurence.status == 'Pending')
                {
                    this.$router.push({name: "OccurencesList", params: { id: this.occurence.id, status: PENDING_STATUS } });
                }
                else
                {
                    this.$router.push({name: "OccurencesList", params: { status: this.occurence.status } });
                }
            }
            this.submitting = false;
        },
        genericValidator,
        errorValidator,
        onImagePicked(val)
        {
            this.occurence.photos = [{url: API_HELPER.pathJoin([process.env.VUE_APP_DEFAULT_IMAGES_ROOT, val])}];
            this.showDefaultImagePopUp = false;
        },
        coordinateChanged()
        {
            // Avoid obtaining address on non manual changes
            if(this.setup)
            {
                this.getAddress({latitude: this.occurence.latitude, longitude: this.occurence.longitude});
            }
        },
        // handleBackButton() {
        //     console.log('User clicked the back button');
        //     this.$router.push({name: "OccurencesList", params: { status: localStorage.getItem('status') } })
        // }
    },
    // created() {
    //   window.addEventListener('popstate', this.handleBackButton);
    // },
    // destroyed() {
    //   window.removeEventListener('popstate', this.handleBackButton);
    // },  
    watch:
    {
        postOccurenceOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        putOccurenceOutput: function(val)
        {
            if(val)
            {
                this.showConfirmationPopup = true;
            }
        },
        baseOccurence: function()
        {
            this.resetOccurence();
        },
        alertType: function()
        {
            this.submitting = false;
        },
        alertOutput: function()
        {
            this.submitting = false;

            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        },
        getAddressOutput: function(val)
        {
            if(val && val.display_name)
            {
                this.occurence.address = val.display_name;
                this.$forceUpdate(); // Necessary to correctly update the UI
            }
        },
    }
  }
</script> 
 
<style scoped>
>>>input[type="checkbox"]
{
    height: 25px;
    width: 25px;
}

.vue-tags-input
{
    max-width: unset;
}

#photo-map-container
{
    min-width: 300px;
}

#show-default-img-popup
{
    border-color: #d8dbe0;
    border-top-color: rgb(216, 219, 224);
    border-right-color: rgb(216, 219, 224);
    border-bottom-color: rgb(216, 219, 224);
    border-left-color: rgb(216, 219, 224);
    border-radius: 0.25rem;
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
    border-bottom-left-radius: 0.25rem;
}
.btn-coordinates{
    width: 200px;
    height: 35px;
    margin-top: 29px;
}
</style> 